// The pile goes in at once.
//
// WHAT THIS IS FOR. A manager comes back from a shift handover holding forty
// pieces of paper: deposit slips, petrol receipts, and somewhere in the pile the
// month's bank statement. The first version of this dialog asked him to pick one
// file, say what it was and say whose it was, and then do that thirty-nine more
// times. A control that costs forty rounds of three questions is a control that
// ends in a drawer, and a slip in a drawer proves nothing.
//
// So: drop the lot, or choose the lot, and the files are read one after another
// and each says what it is.
//
// FOUR DECISIONS WORTH KNOWING BEFORE CHANGING ANY OF THIS.
//
// 1. THE FILE SAYS WHAT IT IS, a person does not. A bank statement prints a
//    statement period, an opening balance and a run of dated lines carrying a
//    running balance, and nothing else in this pile does. Slips and receipts are
//    told apart on the words they use. Every answer is a PROPOSAL that lands on
//    the row and is changed on the board before anything is confirmed.
//
// 2. A STATEMENT LEAVES BY THE OTHER DOOR. Uploading a slip is canView; loading a
//    statement is finance plus can_manage_bank_details. When a file classifies as
//    a statement nothing is stored here and the page sends that one file to the
//    statement loader, which checks it against its own totals first. A caller
//    without that permission is told, per file, rather than silently refused.
//
// 3. ONE AT A TIME, NOT FORTY AT ONCE. Each file is base64 through
//    cockpit_edge_dispatch and this project's connection pool is sixty wide.
//    CLAUDE.md section 7 records a single looping statement taking the whole
//    cockpit down for forty minutes. A file that fails is recorded on its row and
//    the batch carries on.
//
// 4. THE DRIVER IS OPTIONAL HERE AND REQUIRED AT CONFIRMATION. "Whose are these"
//    applies to the whole batch and may be left empty, which is what makes a pile
//    for the whole company possible. Naming the man is the act that turns a
//    document into evidence, and it happens on the board with the document open
//    beside the figure.
import { useCallback, useRef, useState } from 'react'
import { Upload, AlertCircle, FileText, X, Check, Landmark } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Label } from '../../lib/shadcn/label'
import { Chip } from '../interviews/Chip'
import { zar } from './cashModel'

const MAX_BYTES = 15 * 1024 * 1024

export type DriverOption = { driver_id: string; driver_name: string }

export type IntakeKind = 'auto' | 'deposit_slip' | 'fuel_receipt' | 'other'

export type IntakeRow = {
  name: string
  state: 'waiting' | 'sending' | 'done' | 'failed' | 'skipped'
  // What it turned out to be, once something has read it.
  kind?: string
  amount_zar?: number | null
  doc_date?: string | null
  message: string
}

export type IntakeFile = { file: File; contentBase64: string }

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

export function kindLabel(kind: string | undefined): string {
  if (kind === 'deposit_slip') return 'Deposit slip'
  if (kind === 'fuel_receipt') return 'Petrol receipt'
  if (kind === 'bank_statement') return 'Bank statement'
  if (kind === 'other') return 'Not recognised'
  return 'Document'
}

export function CashDocumentUpload({
  drivers,
  busy,
  rows,
  mayLoadStatements,
  defaultDriverId,
  onCancel,
  onSubmit,
}: {
  drivers: DriverOption[]
  busy: boolean
  // One row per file in the batch, in the order they will be sent.
  rows: IntakeRow[]
  // Whether a bank statement found in the pile can be loaded by this person, so
  // the dialog can say what will happen to it before anything is sent.
  mayLoadStatements: boolean
  // The driver this was opened from, where it was opened from his own row. It
  // fills the picker and does not lock it: a manager who opened the wrong man's
  // row should fix it here rather than close and start again.
  defaultDriverId?: string
  onCancel: () => void
  onSubmit: (args: { kind: IntakeKind; driver_id: string | null; files: IntakeFile[] }) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [kind, setKind] = useState<IntakeKind>('auto')
  const [driverId, setDriverId] = useState(defaultDriverId ?? '')
  const [files, setFiles] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)

  const tooLarge = files.filter((f) => f.size > MAX_BYTES)
  const usable = files.filter((f) => f.size <= MAX_BYTES)
  const started = rows.length > 0

  // A drop replaces nothing: a manager who drops two handfuls means both.
  const add = useCallback((incoming: File[]) => {
    setFiles((current) => {
      const seen = new Set(current.map((f) => `${f.name}:${f.size}`))
      return [...current, ...incoming.filter((f) => !seen.has(`${f.name}:${f.size}`))]
    })
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg border border-border bg-card shadow-lg">
        <div className="flex items-start gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">Add documents</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Deposit slips, petrol receipts and bank statements, as many at once as you are
              holding. Each one is read and says what it is. Nothing it says counts towards any
              figure until somebody confirms it against the document.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="ml-auto shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">What are they</Label>
              <NativeSelect
                value={kind}
                disabled={busy || started}
                onChange={(e) => setKind(e.target.value as IntakeKind)}
              >
                <option value="auto">Read it off each document</option>
                <option value="deposit_slip">All deposit slips</option>
                <option value="fuel_receipt">All petrol receipts</option>
                <option value="other">Something else</option>
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Whose are they</Label>
              <NativeSelect
                value={driverId}
                disabled={busy || started}
                onChange={(e) => setDriverId(e.target.value)}
              >
                <option value="">Mixed, or not sure yet</option>
                {drivers.map((d) => (
                  <option key={d.driver_id} value={d.driver_id}>
                    {d.driver_name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Both are optional. Leave them as they are and drop the whole pile for the company: what
            each document is gets read off it, and whose it is gets settled on the board with the
            document open beside the figure, which is the moment it becomes evidence.
          </p>

          <div
            onDragOver={(e) => {
              e.preventDefault()
              if (!busy) setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              if (busy) return
              add(Array.from(e.dataTransfer.files ?? []))
            }}
            className={
              dragging
                ? 'flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-8 text-center'
                : 'flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-border p-8 text-center'
            }
          >
            <Upload className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {files.length === 0
                ? 'Drop the pile here, or choose the files.'
                : `${files.length} file${files.length === 1 ? '' : 's'} ready`}
            </p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.pdf"
              className="hidden"
              disabled={busy}
              onChange={(e) => add(Array.from(e.target.files ?? []))}
            />
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
                Choose files
              </Button>
              {files.length > 0 && !busy && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFiles([])
                    if (inputRef.current) inputRef.current.value = ''
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
            {!mayLoadStatements && (
              <p className="text-[11px] text-muted-foreground">
                A bank statement in the pile will be named and skipped: loading one needs the bank
                statement permission, which this login does not hold.
              </p>
            )}
          </div>

          {files.length > 0 && (
            <ul className="max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border text-xs">
              {files.map((f) => {
                const row = rows.find((r) => r.name === f.name)
                const state = row?.state ?? 'waiting'
                return (
                  <li key={`${f.name}:${f.size}`} className="flex items-start gap-2 px-3 py-2">
                    {state === 'done' ? (
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    ) : state === 'failed' ? (
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                    ) : state === 'skipped' ? (
                      <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    ) : state === 'sending' ? (
                      <Spinner className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="truncate text-foreground">{f.name}</span>
                        {row?.kind && <Chip tone={row.kind === 'bank_statement' ? 'brand' : 'neutral'}>{kindLabel(row.kind)}</Chip>}
                        {row?.amount_zar != null && (
                          <span className="tabular-nums text-muted-foreground">{zar(row.amount_zar)}</span>
                        )}
                        {row?.doc_date && <span className="text-muted-foreground">{row.doc_date}</span>}
                      </div>
                      <div className={state === 'failed' ? 'text-destructive' : 'text-muted-foreground'}>
                        {f.size > MAX_BYTES
                          ? 'Larger than 15 MB and will not be sent. Photograph it rather than scanning at full resolution.'
                          : (row?.message ??
                            (state === 'sending' ? 'Storing and reading' : 'Waiting'))}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {tooLarge.length > 0 && (
            <p className="text-xs text-destructive">
              {tooLarge.length} of {files.length} are larger than 15 MB and will be skipped. The rest
              still go in.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Close
          </Button>
          <Button
            type="button"
            disabled={usable.length === 0 || busy}
            onClick={async () => {
              const payload = await Promise.all(
                usable.map(async (file) => ({ file, contentBase64: await readAsBase64(file) })),
              )
              onSubmit({ kind, driver_id: driverId || null, files: payload })
            }}
          >
            {busy ? <Spinner className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
            Read and file {usable.length > 0 ? usable.length : ''}
          </Button>
        </div>
      </div>
    </div>
  )
}
